<template>
	<Panel title="Insurance Information">
		<template #HeaderComponent>
			<div class="panel-header-button-array">
				<button
					v-if="isInsuranceVerified"
					class="button"
					@click="autoFillInsuranceFromAutoComplete"
				>
					Auto-Fill Insurance Data
				</button>
				<button
					v-if="isInsuranceVerified"
					class="button button-load ignore-all-locks"
					@click="openAutoCompleteModal"
				>
					View AutoComplete Data
				</button>
			</div>
		</template>

		<div :style="$grid('2-1.5-1.5-1.5-2')">
			<InputTextbox
				label="Insurance Company Name"
				v-model:saturn="insurance.companyName"
				:modelValue="insurance.companyName"
				:invalid="util.isNull(validation) ? false : validation.companyName.$invalid"
				:infoIconObj="autoCompleteTooltips.companyName"
				placeholder="Insurance Company Name"
			>
				<template #validation>
					<div v-if="!util.isNull(validation) && validation.companyName.required?.$invalid">Insurance Company Name is required</div>
				</template>
			</InputTextbox>
			<InputTextbox
				label="NAIC Number"
				v-model:saturn="insurance.naic"
				:modelValue="insurance.naic"
				:invalid="util.isNull(validation) ? false : validation.naic.$invalid"
				placeholder="NAIC Number"
			>
				<template #validation>
					<div v-if="!util.isNull(validation) && validation.naic.required?.$invalid">NAIC Number is required</div>
				</template>
			</InputTextbox>
			<InputTextbox
				label="Policy Number"
				v-model:saturn="insurance.policyNumber"
				:modelValue="insurance.policyNumber"
				:invalid="util.isNull(validation) ? false : validation.policyNumber.$invalid"
				:infoIconObj="autoCompleteTooltips.policyNumber"
				placeholder="Policy Number"
				:isUpperCase="true"
			>
				<template #validation>
					<div v-if="!util.isNull(validation) && validation.policyNumber.required?.$invalid">Policy Number is required</div>
				</template>
			</InputTextbox>
			<InputPhone
				label="Phone Number"
				type="tel"
				v-model:saturn="insurance.agencyPhoneNumber"
				:modelValue="insurance.agencyPhoneNumber"
				:invalid="util.isNull(validation) ? false : validation.agencyPhoneNumber.$invalid"
				placeholder="Phone Number"
			>
				<template #validation>
					<div v-if="!util.isNull(validation) && validation.agencyPhoneNumber.required?.$invalid">Phone Number is required</div>
					<div v-if="!util.isNull(validation) && validation.agencyPhoneNumber.minLength.$invalid">Phone Number must be 10 characters.</div>
				</template>
			</InputPhone>
			<InputTextbox
				label="Agency Name"
				v-model:saturn="insurance.agencyName"
				:modelValue="insurance.agencyName"
				:invalid="util.isNull(validation) ? false : validation?.agencyName.$invalid"
				placeholder="Agency Name"
			>
				<template #validation>
					<div v-if="!util.isNull(validation) && validation.agencyName.required?.$invalid">Agency Name is required</div>
				</template>
			</InputTextbox>
		</div>
		<div :style="$grid('1')">
			<AddressComponent
				:data="insurance.agencyAddress"
				:validation="validation?.agencyAddress"
				:panelView="true"
				:showCounty="true"
				:searchable="true"
				:overrideValidation="overrideValidation"
				:objectStructure="{
					address: 'addressLine1',
					addressExtra: 'addressLine2',
					zip: 'zipCode',
					city: 'city',
					state: 'state',
					country: 'country',
					county: 'county',
					usingAddressExtra: 'usingAddressExtra',
					usingUSAddress: 'usingUSAddress',
				}"
				countryQuestionText="Is the address in the US?"
				:fallbackzipcode="fallBackZipCode"
			/>
		</div>
		<div :style="$grid('1-1')">
			<InputTextbox
				label="Verified By"
				v-model:saturn="insurance.verifiedBy"
				:modelValue="insurance.verifiedBy"
				:invalid="util.isNull(validation) ? false : validation.verifiedBy.$invalid"
				placeholder="Verified By"
			>
				<template #validation>
					<div v-if="!util.isNull(validation) && validation.verifiedBy.required?.$invalid">Verified By is required</div>
				</template>
			</InputTextbox>
			<InputTextbox
				label="Spoke With"
				v-model:saturn="insurance.agentName"
				:modelValue="insurance.agentName"
				:invalid="util.isNull(validation) ? false : validation.agentName.$invalid"
				placeholder="Spoke With"
			>
				<template #validation>
					<div v-if="!util.isNull(validation) && validation.agentName.required?.$invalid">Spoke With is required</div>
				</template>
			</InputTextbox>
		</div>
		<div
			:style="$grid('1-1-1-1-1')"
			class="no-margin-grid"
		>
			<InputCurrency
				label="Collision"
				v-model:saturn="insurance.collision"
				:modelValue="insurance.collision"
				:infoIconObj="autoCompleteTooltips.collision"
				placeholder="Collison"
				:invalid="util.isNull(validation) ? false : validation.collision.$invalid"
			>
				<template #validation>
					<div v-if="!util.isNull(validation) && validation.collision.required?.$invalid">Collision is required</div>
					<div v-if="!util.isNull(validation) && validation.collision.minValue?.$invalid">{{ validation.collision.minValue.$message }}.</div>
				</template>
			</InputCurrency>
			<InputCurrency
				label="Comprehensive"
				v-model:saturn="insurance.comprehensive"
				:modelValue="insurance.comprehensive"
				:infoIconObj="autoCompleteTooltips.comprehensive"
				placeholder="Compensation"
				:invalid="util.isNull(validation) ? false : validation.comprehensive.$invalid"
			>
				<template #validation>
					<div v-if="!util.isNull(validation) && validation.comprehensive.required?.$invalid">Comprehensive is required</div>
					<div v-if="!util.isNull(validation) && validation.comprehensive.minValue.$invalid">{{ validation.comprehensive.minValue.$message }}.</div>
				</template>
			</InputCurrency>
			<InputCurrency
				label="Fire / Theft"
				v-model:saturn="insurance.fireTheft"
				:modelValue="insurance.fireTheft"
				placeholder="Fire / Theft"
				:invalid="util.isNull(validation) ? false : validation.fireTheft.$invalid"
			>
				<template #validation>
					<div v-if="!util.isNull(validation) && validation.fireTheft.required?.$invalid">Fire / Theft is required</div>
					<div v-if="!util.isNull(validation) && validation.fireTheft.minValue.$invalid">{{ validation.fireTheft.minValue.$message }}.</div>
				</template>
			</InputCurrency>
			<InputDate
				label="Effective Date"
				v-model:saturn="insurance.effectiveDate"
				:modelValue="insurance.effectiveDate"
				:invalid="util.isNull(validation) ? false : validation.effectiveDate.$invalid"
				:infoIconObj="autoCompleteTooltips.effectiveDate"
				placeholder="Effective Date"
				:key="effectiveDateKey"
			>
				<template #validation>
					<div v-if="!util.isNull(validation) && validation.effectiveDate.required?.$invalid">Effective Date date is required</div>
					<div v-else-if="!util.isNull(validation) && validation.effectiveDate.valid.$invalid">
						Effective Date must be today or a past date, not more than 12 months old.
					</div>
				</template>
			</InputDate>
			<InputDate
				label="Expiration Date"
				v-model:saturn="insurance.expirationDate"
				:modelValue="insurance.expirationDate"
				:invalid="util.isNull(validation) ? false : validation.expirationDate.$invalid"
				:infoIconObj="autoCompleteTooltips.expirationDate"
				placeholder="Expiration Date"
				:key="expirationDateKey"
			>
				<template #validation>
					<div v-if="!util.isNull(validation) && validation.expirationDate.required?.$invalid">Expiration Date date is required</div>
					<div v-else-if="!util.isNull(validation) && validation.expirationDate.valid.$invalid">
						Expiration Date has to be a future date, after effective date and less than 12 months
					</div>
				</template>
			</InputDate>
		</div>
	</Panel>
</template>
<script lang="ts">
	export interface IPanelInsuranceProps {
		isLoading: boolean;
		isAutoCompleteEnabled: boolean;
		fallBackZipCode: string;
		insurance: FIMenuInsurance;
		validation?: any;
		autoCompleteVerification?: AutoCompleteVerificationResponse;
		overrideValidation?: boolean;
	}
</script>
<script setup lang="ts">
	import { computed, ref } from 'vue';

	import $modal from '@core/services/modal';
	import util from '@core/services/util';

	import { AutoCompleteCoverage, AutoCompleteDetailedPolicy } from '@core/classes/Insurance/AutoCompleteModels';
	import { AutoCompleteVerificationResponse } from '@core/classes/Insurance/AutoCompleteResponses';
	import FIMenuInsurance from '@core/classes/FIMenuInsurance';

	import modalAutoCompleteResults from '@/modals/modalAutoCompleteResults.vue';

	import AddressComponent from '@/components/AddressComponent.vue';
	import InputCurrency from '@core/components/InputCurrency.vue';
	import InputDate from '@core/components/InputDate.vue';
	import InputPhone from '@core/components/InputPhone.vue';
	import InputTextbox from '@core/components/InputTextbox.vue';
	import Panel from '@core/components/Panel.vue';

	interface IAutoCompleteTooltip {
		icon: string;
		tooltipText: string[];
	}

	interface IAutoCompleteTooltipList {
		companyName?: IAutoCompleteTooltip;
		policyNumber?: IAutoCompleteTooltip;
		effectiveDate?: IAutoCompleteTooltip;
		expirationDate?: IAutoCompleteTooltip;
		collision?: IAutoCompleteTooltip;
		comprehensive?: IAutoCompleteTooltip;
	}

	const props = defineProps<IPanelInsuranceProps>();

	const effectiveDateKey = ref(0);
	const expirationDateKey = ref(0);

	const isInsuranceVerified = computed((): boolean => {
		return props.autoCompleteVerification?.status === 'success';
	});

	const verifiedPolicy = computed((): AutoCompleteDetailedPolicy => {
		const policyList = props.autoCompleteVerification?.person?.policies;
		return policyList && policyList.length > 0 ? policyList[0] : null;
	});

	const coverages = computed((): AutoCompleteCoverage => verifiedPolicy.value?.coverages);

	const acCollision = computed((): number => {
		// Converting cents to dollars
		return coverages.value?.collision?.deductible ? Number(coverages.value.collision.deductible / 100) : null;
	});

	const acComprehensive = computed((): number => {
		// Converting cents to dollars
		return coverages.value?.comprehensive?.deductible ? Number(coverages.value.comprehensive.deductible / 100) : null;
	});

	const autoCompleteTooltips = computed((): IAutoCompleteTooltipList => {
		if (!props.isAutoCompleteEnabled || !isInsuranceVerified.value)
			return {
				companyName: null,
				policyNumber: null,
				effectiveDate: null,
				expirationDate: null,
				collision: null,
				comprehensive: null,
			};

		const defaultIconObj = { icon: 'fas fa-info-circle' };
		const defaultTooltipText = 'Value found by AutoComplete:';

		// Converting cents to dollars
		const acCollisionDisplay = acCollision.value?.toFixed(2);
		const acComprehensiveDisplay = acComprehensive.value?.toFixed(2);

		return {
			companyName: verifiedPolicy.value?.carrierName
				? {
						...defaultIconObj,
						tooltipText: [`${defaultTooltipText} ${verifiedPolicy.value.carrierName.toUpperCase()}`],
					}
				: null,
			policyNumber: verifiedPolicy.value?.carrierPolicyNumber
				? {
						...defaultIconObj,
						tooltipText: [`${defaultTooltipText} ${verifiedPolicy.value.carrierPolicyNumber.toUpperCase()}`],
					}
				: null,
			effectiveDate: verifiedPolicy.value?.effectiveDate
				? {
						...defaultIconObj,
						tooltipText: [`${defaultTooltipText} ${new Date(verifiedPolicy.value.effectiveDate).toLocaleDateString()}`],
					}
				: null,
			expirationDate: verifiedPolicy.value?.expiryDate
				? {
						...defaultIconObj,
						tooltipText: [`${defaultTooltipText} ${new Date(verifiedPolicy.value.expiryDate).toLocaleDateString()}`],
					}
				: null,
			collision: acCollisionDisplay
				? {
						...defaultIconObj,
						tooltipText: [`${defaultTooltipText} $${acCollisionDisplay}`],
					}
				: null,
			comprehensive: acComprehensiveDisplay
				? {
						...defaultIconObj,
						tooltipText: [`${defaultTooltipText} $${acComprehensiveDisplay}`],
					}
				: null,
		};
	});

	function openAutoCompleteModal() {
		$modal.open(modalAutoCompleteResults, {
			name: 'modalAutoCompleteResults',
			passedData: {
				autoCompleteVerification: props.autoCompleteVerification,
			},
		});
	}

	function autoFillInsuranceFromAutoComplete() {
		if (!verifiedPolicy.value) return;

		if (verifiedPolicy.value.carrierName) props.insurance.companyName = verifiedPolicy.value.carrierName;

		if (verifiedPolicy.value.carrierPolicyNumber) props.insurance.policyNumber = verifiedPolicy.value.carrierPolicyNumber;

		if (acCollision.value) props.insurance.collision = acCollision.value;

		if (acComprehensive.value) props.insurance.comprehensive = acComprehensive.value;

		if (verifiedPolicy.value.effectiveDate) props.insurance.effectiveDate = formatDate(verifiedPolicy.value.effectiveDate);

		if (verifiedPolicy.value.expiryDate) props.insurance.expirationDate = formatDate(verifiedPolicy.value.expiryDate);
	}

	function formatDate(dateStr: string | Date): string {
		return util.formatDateWithOptions(new Date(dateStr), { month: '2-digit', day: '2-digit', year: 'numeric' });
	}
</script>
